import { Link } from "react-router-dom";
import type { Artist, ArtistXP } from "@/mocks/artistData";

interface Props {
  artist: Artist;
  xpData?: ArtistXP;
  totalRewards: number;
  redeemableCount: number;
  artistQuestCount: number;
}

export default function ArtistXPCard({ artist, xpData, totalRewards, redeemableCount, artistQuestCount }: Props) {
  const xp = xpData?.xp ?? 0;
  const level = xpData?.level ?? 0;
  const levelName = xpData?.levelName ?? "신규 팬";
  const nextLevelXP = xpData?.nextLevelXP ?? 5000;
  const totalEarned = xpData?.totalEarned ?? 0;
  const questsCompleted = xpData?.questsCompleted ?? 0;
  const progressPercent = Math.min(Math.round((xp / nextLevelXP) * 100), 100);

  return (
    <div
      className="rounded-2xl overflow-hidden relative"
      style={{ border: `1px solid ${artist.accentColor}30`, background: `linear-gradient(135deg, ${artist.accentColor}10, rgba(18,18,26,0.95))` }}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5 -translate-y-1/2 translate-x-1/2"
        style={{ background: artist.accentColor }}
      />

      <div className="relative z-10 p-6 md:p-8">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* XP Main */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl"
                style={{ background: `${artist.accentColor}20`, border: `1px solid ${artist.accentColor}40` }}
              >
                <i className="ri-coin-fill text-lg" style={{ color: artist.accentColor }} />
              </div>
              <div>
                <div className="text-white/40 text-xs">내 {artist.name} XP</div>
                <div className="text-white/60 text-xs">
                  이 XP는 <strong className="text-white">{artist.name}</strong> 리워드에만 사용 가능합니다
                </div>
              </div>
            </div>

            <div className="flex items-end gap-3 mb-5">
              <span className="font-black text-5xl" style={{ color: artist.accentColor }}>
                {xp.toLocaleString()}
              </span>
              <span className="text-white/40 text-lg mb-1">XP</span>
              <div
                className="ml-2 mb-1 px-3 py-1 rounded-full text-xs font-bold"
                style={{ background: `${artist.accentColor}20`, color: artist.accentColor, border: `1px solid ${artist.accentColor}40` }}
              >
                Lv.{level} {levelName}
              </div>
            </div>

            {/* Level Progress */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/40 text-xs">다음 레벨까지</span>
                <span className="text-white/60 text-xs font-semibold">
                  {xp.toLocaleString()} / {nextLevelXP.toLocaleString()} XP
                </span>
              </div>
              <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${progressPercent}%`, background: `linear-gradient(to right, ${artist.accentColor}, ${artist.secondaryColor})` }}
                />
              </div>
              <div className="text-right mt-1">
                <span className="text-white/30 text-[10px]">{progressPercent}% 달성</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px self-stretch bg-white/10" />

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-4 lg:w-64">
            {[
              { label: "총 획득 XP", value: totalEarned.toLocaleString(), unit: "XP", icon: "ri-trophy-line", color: "#ffd60a" },
              { label: "완료 퀘스트", value: questsCompleted.toString(), unit: "개", icon: "ri-sword-line", color: artist.accentColor },
              { label: "전체 리워드", value: totalRewards.toString(), unit: "개", icon: "ri-gift-line", color: "#c77dff" },
              { label: "교환 가능", value: redeemableCount.toString(), unit: "개", icon: "ri-exchange-line", color: "#39ff14" },
            ].map((stat) => (
              <div key={stat.label} className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg mb-2" style={{ background: `${stat.color}15` }}>
                  <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-white font-black text-lg">{stat.value}</span>
                  <span className="text-white/30 text-xs mb-0.5">{stat.unit}</span>
                </div>
                <div className="text-white/40 text-[10px]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How to earn XP */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-white/40 text-xs">
              <i className="ri-information-line" />
              <span>{artist.name} XP 획득 방법:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { icon: "ri-sword-line", label: "퀘스트 완료", desc: "+XP" },
                { icon: "ri-heart-line", label: "좋아요 받기", desc: "+10 XP" },
                { icon: "ri-share-line", label: "공유하기", desc: "+5 XP" },
                { icon: "ri-calendar-check-line", label: "연속 참여", desc: "보너스" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <i className={`${item.icon} text-xs`} style={{ color: artist.accentColor }} />
                  <span className="text-white/50 text-[10px]">{item.label}</span>
                  <span className="text-white/30 text-[10px]">{item.desc}</span>
                </div>
              ))}
            </div>
            <Link
              to={`/quests?artist=${artist.id}`}
              className="sm:ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap text-[#0a0a0f]"
              style={{ background: artist.accentColor }}
            >
              <i className="ri-add-line" />
              XP 획득하기
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
